import type { WidgetType } from "./types";

/**
 * O que cada tipo de widget PRECISA do resto do sistema.
 *
 * Existe por um defeito que se repetiu QUATRO vezes ao introduzir tipos novos:
 * um `WidgetType` novo precisa ser reconhecido em pontos espalhados
 * (`historyWindowForWidget`, `applyWidgetWindow`, `widgetDefinesSignalIdentity`,
 * `WIDE_TYPES`), todos escritos como `if (widgetType === "a" || ... )`. Esquecer
 * um deles nao quebra a compilacao nem nenhum teste existente — produz um
 * defeito silencioso:
 *
 *  - fora de `historyWindowForWidget`, o `multi-chart` nunca pedia serie e
 *    nascia eternamente vazio;
 *  - fora de `applyWidgetWindow`, o seletor de periodo era um no-op;
 *  - fora de `widgetDefinesSignalIdentity`, os comparativos gravavam o proprio
 *    titulo no `SignalConfig` da fonte ancora.
 *
 * Sendo um `Record<WidgetType, ...>` COMPLETO, o compilador passa a exigir que
 * todo tipo novo responda as cinco perguntas. O esquecimento vira erro de
 * build em vez de bug em campo.
 *
 * Estas sao CAPACIDADES ESTATICAS, nao decisoes de runtime: `production-kpi`
 * declara `history: true` porque PODE pedir serie, e a condicao real (modo
 * `total` com janela definida) continua onde sempre esteve. A tabela responde
 * "este tipo participa deste mecanismo?", nao "participa agora?".
 */
export interface WidgetCapabilities {
  /** Pode pedir serie historica ao Timescale. */
  history: boolean;
  /** Tem seletor de janela (24h/48h/7d/30d) no card. */
  window: boolean;
  /**
   * Escreve a IDENTIDADE do sinal (rotulo/unidade/escala) no `SignalConfig` da
   * fonte. `false` para vistas cuja identidade e da VISAO e nao do sinal —
   * porcentagem de disponibilidade, titulo agregado de um comparativo.
   */
  definesSignalIdentity: boolean;
  /** Ocupa 2 colunas por padrao, enquanto o operador nao escolhe o tamanho. */
  wide: boolean;
  /** Pode ser substituido por um grafico temporal pelo menu de Visualizacao. */
  canGenerateChart: boolean;
  /**
   * Tamanho na malha de celulas (ver `cellGrid.ts`).
   *
   * `min` e o menor tamanho em que o card ainda se le: abaixo dele o arrasto
   * trava. `default` e o tamanho de nascimento.
   *
   * Mora AQUI porque e a sexta pergunta da mesma familia — "de quanto espaco
   * este tipo precisa?". Derivar de `wide` e `history` parecia economico e
   * estava errado: um gauge nao e largo nem tem historico, e ainda assim
   * desenha um mostrador que nao cabe em duas celulas de altura. Deu card
   * cortado na tela.
   */
  cells: { min: { w: number; h: number }; default: { w: number; h: number } };
}

export const WIDGET_CAPABILITIES: Record<WidgetType, WidgetCapabilities> = {
  "state-value": { history: false, window: false, definesSignalIdentity: true, wide: false, canGenerateChart: false, cells: { min: { w: 3, h: 3 }, default: { w: 4, h: 3 } } },
  number: { history: false, window: false, definesSignalIdentity: true, wide: false, canGenerateChart: true, cells: { min: { w: 3, h: 2 }, default: { w: 4, h: 2 } } },
  led: { history: false, window: false, definesSignalIdentity: true, wide: false, canGenerateChart: false, cells: { min: { w: 3, h: 2 }, default: { w: 4, h: 2 } } },
  gauge: { history: false, window: false, definesSignalIdentity: true, wide: false, canGenerateChart: true, cells: { min: { w: 4, h: 3 }, default: { w: 4, h: 4 } } },
  // `progress` e leitura numerica como `number`/`gauge`/`tank`, e ganhou o
  // grafico junto com eles: a excecao anterior nao tinha razao — so faltava
  // marcar a linha.
  progress: { history: false, window: false, definesSignalIdentity: true, wide: false, canGenerateChart: true, cells: { min: { w: 3, h: 2 }, default: { w: 4, h: 3 } } },
  // Disponibilidade/utilizacao expressam o sinal como PORCENTAGEM: a unidade
  // "%" e a escala 0-100 pertencem a visao, nao ao sinal.
  // O grafico da disponibilidade NAO e a serie do sinal — e a linha do tempo
  // rodando x parado, ou seja, o proprio `utilization`. Por isso o card pode
  // gerar grafico mesmo ancorado num sinal de estado, que `isChartableSource`
  // recusaria: quem vira serie aqui e a disponibilidade calculada, nao o `s`.
  availability: { history: false, window: false, definesSignalIdentity: false, wide: false, canGenerateChart: true, cells: { min: { w: 4, h: 3 }, default: { w: 5, h: 4 } } },
  utilization: { history: false, window: true, definesSignalIdentity: false, wide: true, canGenerateChart: false, cells: { min: { w: 5, h: 4 }, default: { w: 7, h: 4 } } },
  "production-status": { history: false, window: false, definesSignalIdentity: true, wide: false, canGenerateChart: false, cells: { min: { w: 3, h: 2 }, default: { w: 4, h: 2 } } },
  "production-kpi": { history: true, window: true, definesSignalIdentity: true, wide: false, canGenerateChart: true, cells: { min: { w: 4, h: 3 }, default: { w: 5, h: 4 } } },
  "line-chart": { history: true, window: true, definesSignalIdentity: false, wide: true, canGenerateChart: false, cells: { min: { w: 5, h: 4 }, default: { w: 7, h: 5 } } },
  "bar-chart": { history: true, window: true, definesSignalIdentity: false, wide: true, canGenerateChart: false, cells: { min: { w: 5, h: 4 }, default: { w: 7, h: 5 } } },
  // Comparativos: o titulo descreve o CONJUNTO, nao a fonte ancora.
  "multi-value": { history: false, window: false, definesSignalIdentity: false, wide: true, canGenerateChart: true, cells: { min: { w: 5, h: 2 }, default: { w: 7, h: 3 } } },
  "multi-chart": { history: true, window: true, definesSignalIdentity: false, wide: true, canGenerateChart: false, cells: { min: { w: 6, h: 4 }, default: { w: 8, h: 5 } } },
  // O tanque LE a identidade do sinal (unidade e faixa sao do sensor, nao da
  // visao), entao a define como qualquer card de leitura direta.
  tank: { history: false, window: false, definesSignalIdentity: true, wide: false, canGenerateChart: true, cells: { min: { w: 3, h: 3 }, default: { w: 4, h: 4 } } },
  // Diagnostico de BARRAMENTO: nao descreve grandeza nenhuma, entao nao pode
  // escrever a identidade da fonte ancora — que serve so para achar o slot.
  "modbus-health": {
    history: false,
    window: false,
    definesSignalIdentity: false,
    wide: true,
    canGenerateChart: false,
    cells: { min: { w: 4, h: 3 }, default: { w: 5, h: 4 } },
  },
};

export function widgetCapabilities(widgetType: WidgetType): WidgetCapabilities {
  return WIDGET_CAPABILITIES[widgetType];
}
